import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";

/** Teams are the roster's teams. A login is on a team when an admin links
 *  it to that roster person (Admin → Teams). */
export interface TeamMember {
  id: number;
  display_name: string | null;
  email: string;
}

export interface Team {
  name: string;
  /** Roster headcount; `members` are the linked planner logins. */
  people: number;
  members: TeamMember[];
}

export interface RosterLogin {
  name: string;
  team: string | null;
  user_id: number | null;
  user_display_name: string | null;
  user_email: string | null;
}

const TEAMS = ["teams"];
const LOGINS = ["roster-logins"];

function errorDetail(err: unknown, fallback: string): string {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export function useTeams() {
  return useQuery<Team[]>({
    queryKey: TEAMS,
    queryFn: () => api.get("/teams").then((r) => r.data),
  });
}

export function useRosterLogins() {
  return useQuery<RosterLogin[]>({
    queryKey: LOGINS,
    queryFn: () => api.get("/admin/roster-logins").then((r) => r.data),
  });
}

function useLinkMutation<V>(fn: (v: V) => Promise<unknown>, failure: string, onDone?: (r: unknown) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: LOGINS });
      qc.invalidateQueries({ queryKey: TEAMS });
      onDone?.(r);
    },
    onError: (err) => toast.error(errorDetail(err, failure)),
  });
}

export const useLinkRosterLogin = () =>
  useLinkMutation(
    (v: { name: string; user_id: number | null }) => api.put("/admin/roster-logins", v),
    "Couldn't link the login",
  );

export const useAutoLinkRosterLogins = () =>
  useLinkMutation(
    () => api.post("/admin/roster-logins/auto-link").then((r) => r.data as { linked: number }),
    "Couldn't link logins",
    (r) => {
      const n = (r as { linked: number }).linked;
      toast.success(n ? `Linked ${n} ${n === 1 ? "person" : "people"} by name` : "No new name matches");
    },
  );
